import type { SVGProps } from 'react';

export type NavIconName =
  | 'overview'
  | 'evaluations'
  | 'analytics'
  | 'interventions'
  | 'reports'
  | 'permission-slips'
  | 'parent-portal'
  | 'announcements'
  | 'users'
  | 'districts'
  | 'schools'
  | 'settings';

const stroke = 1.75;

export function NavIcon({
  name,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { name: NavIconName }) {
  const common = {
    className,
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    ...props,
  };

  switch (name) {
    case 'overview':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      );
    case 'evaluations':
      return (
        <svg {...common}>
          <path d="M8 4h8l2 4v12H6V8l2-4z" />
          <path d="M8 8h8" />
          <path d="M9 13h6M9 17h4" />
        </svg>
      );
    case 'analytics':
      return (
        <svg {...common}>
          <path d="M4 19V5" />
          <path d="M4 19h16" />
          <path d="M8 16v-4M12 16V8M16 16v-6" />
        </svg>
      );
    case 'interventions':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 4v2M12 18v2M4 12h2M18 12h2" />
        </svg>
      );
    case 'reports':
      return (
        <svg {...common}>
          <path d="M7 4h7l3 3v13H7V4z" />
          <path d="M14 4v4h4" />
          <path d="M9 13h6M9 17h4" />
        </svg>
      );
    case 'permission-slips':
      return (
        <svg {...common}>
          <path d="M6 4h12v16H6z" />
          <path d="M9 8h6M9 12h6M9 16h4" />
        </svg>
      );
    case 'parent-portal':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="2.5" />
          <circle cx="15" cy="8" r="2.5" />
          <path d="M5 19c0-2.5 2-4 4-4s4 1.5 4 1.5S15 15 17 15s4 1.5 4 4" />
        </svg>
      );
    case 'announcements':
      return (
        <svg {...common}>
          <path d="M5 10v4l-2 2V8l2 2z" />
          <path d="M5 10c2-2 5-3 8-3s6 1 8 3" />
          <path d="M13 7v10" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3" />
          <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
        </svg>
      );
    case 'districts':
      return (
        <svg {...common}>
          <rect x="4" y="8" width="16" height="11" rx="1" />
          <path d="M8 8V6h8v2M12 8v11" />
        </svg>
      );
    case 'schools':
      return (
        <svg {...common}>
          <path d="M3 10l9-5 9 5-9 5-9-5z" />
          <path d="M6 12v5c0 1 2 2 6 2s6-1 6-2v-5" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

const navIconByLabel: Record<string, NavIconName> = {
  Overview: 'overview',
  Evaluations: 'evaluations',
  Analytics: 'analytics',
  Interventions: 'interventions',
  Reports: 'reports',
  'Permission Slips': 'permission-slips',
  'Parent Portal': 'parent-portal',
  Announcements: 'announcements',
  Users: 'users',
  Districts: 'districts',
  Schools: 'schools',
  Settings: 'settings',
};

export function navIconForItem(name: string): NavIconName {
  return navIconByLabel[name] ?? 'overview';
}
